import SettingsIcon from '@mui/icons-material/Settings';
import { Fade, Paper } from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import LeftMenu from './leftMenu/LeftMenu';
import { MenuList } from '../constants/menu';
import { HEADER_HEIGHT, HEADER_SPACING, DRAWER_WIDTH, GUEST_RESTRICTED_PATHS } from '../constants/constants';
import Header from './header/Header';
import { getAllChatMembers } from '../api/chat';
import { useSelector } from 'react-redux';
import LightVersionOverlay from '../components/common/LightVersionOverlay';
import LoginNoticeModal from '../components/common/modal/LoginNoticeModal';

const drawerWidth = DRAWER_WIDTH;

// ============================================
// Drawer Mixins
// ============================================
const openedMixin = theme => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = theme => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(6)} + 1px)`,
  },
});

// ============================================
// Styled Components
// ============================================
const LayoutContainer = styled(Box)({
  display: 'flex',
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
  '& .MuiDrawer-paperAnchorLeft': {
    background: '#222324',
    color: '#fff',
    height: '100vh',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#555',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#777',
    },
  },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '124px',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const LogoContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const LogoPaper = styled(Paper)({
  background: 'transparent',
  boxShadow: 'none',
});

const LogoText = styled(Typography)({
  display: 'flex',
  alignItems: 'center',
  color: '#fff',
  fontSize: '32px',
  marginLeft: '40px',
});

const LogoBold = styled('span')({
  fontSize: '24px',
  fontWeight: 1000,
});

const LogoLight = styled('span')({
  fontSize: '24px',
  fontWeight: 1,
});

const SettingsWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  padding: '20px 22px',
  background: isActive ? 'black' : 'inherit',
  borderRadius: isActive ? '50% 0 0 50%' : 'none',
}));

const StyledSettingsIcon = styled(SettingsIcon)({
  fontSize: '24px',
  padding: '0px !important',
  margin: '0px !important',
  '&:hover': {
    cursor: 'pointer',
    color: '#0F7F85',
  },
});

const MenuContainer = styled('div')({
  background: '#222324',
  color: '#fff',
});

const MainContent = styled(Box)({
  flex: 1,
  minWidth: 0,
  // paddingTop: `${HEADER_HEIGHT + HEADER_SPACING}px`,
  paddingTop: `${HEADER_HEIGHT + 12}px`,
  paddingLeft: '20px',
  paddingRight: '20px',
  paddingBottom: '2%',
  // overflow:hidden 대신 clip 사용 — sticky 컨텍스트를 만들지 않아 자식의 position:sticky가 viewport 기준으로 정상 동작.
  overflowX: 'clip',
});

// ============================================
// Helper Functions
// ============================================

const getMenuId = path => {
  for (let menu of MenuList) {
    if (menu.path?.trim() === path?.trim()) {
      return menu.menuId;
    }
    for (let subMenu of menu.subMenus || []) {
      if (subMenu.path?.trim() === path?.trim()) {
        return subMenu.menuId;
      }
    }
  }
};

// ============================================
// Main Component
// ============================================
export default function MainLayout(props) {
  const location = useLocation();
  const [open, setOpen] = React.useState(true);
  const [isPinned, setIsPinned] = React.useState(true);
  const [expanded, setExpanded] = React.useState(getMenuId(location.pathname));
  const handleAccordion = React.useRef(() => {});

  const token = useSelector(state => state.user.user.token);
  const user = useSelector(state => state.user.user);
  const userMenus = useSelector(state => state.menu.menu);

  // 사용자 메뉴에 /chat 이 있을 때(=채팅 접근 권한)만 unread 를 조회한다.
  const hasChatAccess = Array.isArray(userMenus) && userMenus.some(menu => menu.menuAuthCode === 'COMMUNITY_CHAT_VIEW');

  // 읽지 않은 채팅이 있으면 사이드바(Message management / Chat)에 빨간 점을 표시한다.
  // 백그라운드 폴링이라 에러는 조용히 흡수(토스트 X)하고 배지만 숨긴다. (권한/일시 오류 대비)
  const { data: chatMembers = [] } = useQuery({
    queryKey: ['chatUnread'],
    queryFn: async () => {
      try {
        return await getAllChatMembers(token);
      } catch (error) {
        console.error('채팅 unread 조회 실패:', error);
        return [];
      }
    },
    enabled: !!token && hasChatAccess,
    refetchInterval: 30000,
  });
  const unreadCount = chatMembers.filter(member => !member.read).length;

  const handleChange = panel => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  React.useEffect(() => {
    handleAccordion.current(expanded);
  }, [expanded]);

  const handleMouseEnter = () => {
    if (!isPinned) {
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isPinned) {
      setOpen(false);
    }
  };

  const isOperatorsPage = location.pathname === '/operators';
  const showSettings = !['ROLE_SUPERADMIN', 'ROLE_MANAGER', 'ROLE_EMPLOYEE'].includes(user.userRole);

  // ROLE_GUEST이고 제한된 경로인 경우 블러 처리
  const isGuestUser = user.userRole === 'ROLE_GUEST';
  const isRestrictedPath = GUEST_RESTRICTED_PATHS.some(path => location.pathname.startsWith(path));
  const shouldShowBlur = isGuestUser && isRestrictedPath;

  return (
    <LayoutContainer className="font-face-layout">
      <CssBaseline />
      <LoginNoticeModal />
      <Header open={open} setOpen={setOpen} isPinned={isPinned} setIsPinned={setIsPinned} />
      <Drawer
        transitionDuration={{ enter: 500, exit: 1000 }}
        variant="permanent"
        open={open}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        <DrawerHeader>
          <LogoContainer>
            <Fade in={open}>
              <LogoPaper>
                <LogoText variant="text" noWrap component="div">
                  <LogoBold>BODY</LogoBold>
                  &nbsp;
                  <LogoLight>SWITCH</LogoLight>
                </LogoText>
              </LogoPaper>
            </Fade>
          </LogoContainer>
          <SettingsWrapper isActive={isOperatorsPage}>
            {showSettings && (
              <Fade in={open}>
                <LogoPaper>
                  {/* 2026-03-17 임시로 숨김 */}
                  {/* <Link style={{ color: '#a1a1a1' }} to="/operators">
                    <StyledSettingsIcon onClick={() => setExpanded('panel8')} />
                  </Link> */}
                </LogoPaper>
              </Fade>
            )}
          </SettingsWrapper>
        </DrawerHeader>

        <MenuContainer>
          {MenuList.map((menu, index) => (
            <LeftMenu
              key={index}
              data={menu}
              open={open}
              setExpanded={setExpanded}
              handleChange={handleChange}
              expanded={expanded}
              unreadCount={unreadCount}
            />
          ))}
        </MenuContainer>
      </Drawer>

      <MainContent component="main">
        {shouldShowBlur ? <LightVersionOverlay>{props.children}</LightVersionOverlay> : props.children}
      </MainContent>
    </LayoutContainer>
  );
}
